import React from 'react';
import { useAuth } from 'react-oidc-context';

import { TimersProvider } from './timer';
import { GameStateProvider } from './game';
import { useGame } from '../hooks/use-game';
import { useVersionInfo } from './version-info';
import { LayoutProvider } from './layout-provider';
import { NotificationProvider } from './notification';
import { GameDocumentProvider } from './game-document';
import { useGameTheme } from '../hooks/use-game-theme';
import { canUserAccessGame } from '../utils/auth-roles';
import { FacilitatorChatProvider } from './facilitator-chat';
import { useGameDocument } from '../hooks/use-game-document';
import { AccessDenied } from '../components/authentication';

interface FacilitatorProviderProps {
  gameCode: string;
  children: React.ReactNode;
}

export const FacilitatorProvider = ({
  gameCode,
  ...props
}: FacilitatorProviderProps) => {
  const auth = useAuth();

  // prefetch all data
  const game = useGame(gameCode, true);
  const gameDocument = useGameDocument(gameCode, true);
  const gameTheme = useGameTheme(gameCode, true);

  const { setLoadedGameDocumentVersion } = useVersionInfo();

  React.useEffect(() => {
    if (!gameDocument) {
      return;
    }

    setLoadedGameDocumentVersion(gameDocument.data?.version!);

    // Cleanup/reset
    return () => {
      setLoadedGameDocumentVersion(null);
    };
  }, [setLoadedGameDocumentVersion, gameDocument?.data?.version]);

  const isLoading =
    game.isLoading || gameDocument.isLoading || gameTheme.isLoading;

  // return loading state
  if (isLoading) return <>Loading...</>;

  // validate organization access
  const canAccess = canUserAccessGame(auth.user, game.data?.organisationId);
  if (!canAccess) {
    return (
      <AccessDenied message="You do not have permission to access this game. Your organisation does not match the game's organisation." />
    );
  }

  // return context wrapper
  return (
    <GameDocumentProvider gameCode={gameCode!} isFromFacilitator>
      <TimersProvider gameCode={gameCode!} isFromFacilitator>
        <GameStateProvider gameCode={gameCode!} isFromFacilitator>
          <NotificationProvider>
            <FacilitatorChatProvider>
              <LayoutProvider>{props.children}</LayoutProvider>
            </FacilitatorChatProvider>
          </NotificationProvider>
        </GameStateProvider>
      </TimersProvider>
    </GameDocumentProvider>
  );
};
